'use client';

import { useId, useState } from 'react';
import type { CSSProperties } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import {
  RiChat3Line,
  RiErrorWarningLine,
  RiForbidLine,
  RiLineChartLine,
  RiRocketLine,
  RiUserAddLine,
} from '@remixicon/react';
import { useTranslations } from 'next-intl';

import { ProductSection, SectionHead } from '@/components/product-section';
import { dur, ease } from '@/lib/motion';
import { hl } from '@/lib/rich-text';

import './autopilot.css';

/**
 * "Saatini beklemez, olaya uyanır" — devriye ritmi ve uyandıran olaylar.
 *
 * Üstte 24 saatlik bir cetvel: ince çizgiler devriye, yeşil işaretler olay.
 * Plan seçilince devriye çizgileri sıklaşıyor. Aralıklar ürünün kendisinden
 * (`@mentorivo/shared/operator`): devriye en fazla günlük operatör hakkının
 * yarısını kullanıyor — Starter 12 koşu → en sık 4 saat, Growth 48 → 1 saat,
 * Pro 144 → 30 dakika (devriyenin alt sınırı 30 dk).
 *
 * Olay işaretleri devriyeden BAĞIMSIZ duruyor; plan değişince yerinden
 * oynamıyorlar — anlatılan şey tam olarak bu: olay devriyeyi beklemez.
 * Kampanyanın yayına girişi iki işaret doğuruyor: +2 saat teslimat kontrolü
 * (24 saatlik takip ertesi güne düşüyor, cetvelin dışında; satırda yazılı).
 *
 * Hareket: devriye çizgileri plan değişince opaklık + dikey ölçekle girip
 * çıkıyor (transform/opacity); çizgiler soldan sağa sırayla — "gün boyu".
 * Olay işaretleri sabit.
 */

const PLANS = [
  { key: 'starter', every: 240 },
  { key: 'growth', every: 60 },
  { key: 'pro', every: 30 },
] as const;
type PlanKey = (typeof PLANS)[number]['key'];

/** Cetveldeki olaylar (dakika). Etiket metni `ruler.marks.<key>`. */
const MARKS = [
  { key: 'launch', at: 9 * 60 + 10 },
  { key: 'check', at: 11 * 60 + 10 },
  { key: 'rejected', at: 15 * 60 + 40 },
  { key: 'comment', at: 20 * 60 + 25 },
] as const;

const EVENTS = [
  { key: 'launch', Icon: RiRocketLine },
  { key: 'rejected', Icon: RiForbidLine },
  { key: 'alert', Icon: RiLineChartLine },
  { key: 'failed', Icon: RiErrorWarningLine },
  { key: 'lead', Icon: RiUserAddLine },
  { key: 'comment', Icon: RiChat3Line },
] as const;

const DAY = 24 * 60;
const HOURS = [0, 6, 12, 18, 24];

export function AutopilotWake() {
  const t = useTranslations('featuresAutopilot.wake');
  const uid = useId();
  const [plan, setPlan] = useState<PlanKey>('starter');
  const every = PLANS.find((p) => p.key === plan)!.every;

  /* Devriye çizgileri: gece yarısından itibaren her `every` dakikada bir.
     Anahtar dakikanın kendisi: planlar arasında ortak olan çizgiler (00:00,
     04:00…) yerinde kalıyor, yalnız aradakiler girip çıkıyor — "sıklaştı". */
  const ticks: number[] = [];
  for (let minute = 0; minute < DAY; minute += every) ticks.push(minute);

  return (
    <ProductSection tone="gray" labelledBy={`${uid}-title`}>
      <SectionHead
        id={`${uid}-title`}
        eyebrow={t('eyebrow')}
        title={t.rich('title', { hl })}
        lead={t('lead')}
      />

      <div className="apw">
        <div className="apw-top">
          <div className="apw-plans" role="group" aria-label={t('plansLabel')}>
            {PLANS.map(({ key }) => (
              <button
                key={key}
                type="button"
                className="apw-plan"
                aria-pressed={plan === key}
                onClick={() => setPlan(key)}
              >
                <span className="apw-plan-name">{t(`plans.${key}.name`)}</span>
                <span className="apw-plan-every">{t(`plans.${key}.every`)}</span>
              </button>
            ))}
          </div>
          <p className="apw-legend">
            <span className="apw-legend-item">
              <span className="apw-legend-tick" aria-hidden="true" />
              {t('ruler.patrol', { count: ticks.length })}
            </span>
            <span className="apw-legend-item">
              <span className="apw-legend-mark" aria-hidden="true" />
              {t('ruler.event')}
            </span>
          </p>
        </div>

        {/* Cetvel bir çizim: içeriği yukarıdaki açıklama ve alttaki liste
            düz metin olarak veriyor. */}
        <div className="apw-ruler" aria-hidden="true">
          <div className="apw-track">
            <AnimatePresence initial={false}>
              {ticks.map((minute, index) => (
                <motion.span
                  key={minute}
                  className="apw-tick"
                  style={{ '--at': `${(minute / DAY) * 100}%` } as CSSProperties}
                  initial={{ opacity: 0, scaleY: 0.2 }}
                  animate={{
                    opacity: 1,
                    scaleY: 1,
                    transition: {
                      duration: dur.fast,
                      ease: ease.out,
                      delay: Math.min(index * 0.012, 0.5),
                    },
                  }}
                  exit={{ opacity: 0, transition: { duration: dur.instant } }}
                />
              ))}
            </AnimatePresence>

            {MARKS.map(({ key, at }) => (
              <span
                key={key}
                className="apw-mark"
                style={{ '--at': `${(at / DAY) * 100}%` } as CSSProperties}
              >
                <span className="apw-mark-label">{t(`ruler.marks.${key}`)}</span>
              </span>
            ))}
          </div>

          <div className="apw-hours">
            {HOURS.map((hour) => (
              <span
                key={hour}
                className="apw-hour"
                style={{ '--at': `${(hour / 24) * 100}%` } as CSSProperties}
              >
                {String(hour).padStart(2, '0')}:00
              </span>
            ))}
          </div>
        </div>

        <ul className="apw-events">
          {EVENTS.map(({ key, Icon }) => (
            <li key={key} className="apw-event">
              <Icon size={18} aria-hidden="true" />
              <div>
                <p className="apw-event-name">{t(`events.${key}.name`)}</p>
                <p className="apw-event-does">{t(`events.${key}.does`)}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </ProductSection>
  );
}
